<script setup>
import { computed } from 'vue';
import { smsSegments } from 'dashboard/helper/smsSegments';

const props = defineProps({
  // the inboxes that can send SMS (canSendSms)
  inboxes: { type: Array, default: () => [] },
  // { inbox_id, message }; an empty array right after the action is picked
  modelValue: { type: [Object, Array], default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

// Controlled by the parent: every change emits the whole value, keyed by the inbox id
// (an object would end up in the payload as is).
const value = computed(
  () =>
    (Array.isArray(props.modelValue)
      ? props.modelValue[0]
      : props.modelValue) || {}
);

const update = changes => {
  emit('update:modelValue', {
    inbox_id: null,
    message: '',
    ...value.value,
    ...changes,
  });
};

const inboxOptions = computed(() =>
  props.inboxes.map(inbox => {
    const name = inbox.internal_name || inbox.name;
    return {
      id: inbox.id,
      name: inbox.phone_number ? `${name} (${inbox.phone_number})` : name,
    };
  })
);

const selectedInbox = computed({
  // rules created through the API may hold the id as a string
  get: () =>
    inboxOptions.value.find(
      option =>
        value.value.inbox_id != null &&
        String(option.id) === String(value.value.inbox_id)
    ) || null,
  set: option => update({ inbox_id: option?.id ?? null }),
});

// Plain text, as in the SMS reply box: what is typed is what the contact gets
const message = computed({
  get: () => value.value.message || '',
  set: newMessage => update({ message: newMessage }),
});

// Counted before the variables are filled in, so only a guide
const counter = computed(() => smsSegments(message.value));
</script>

<template>
  <div class="flex flex-col gap-1 mt-1">
    <div class="multiselect-wrap--small">
      <multiselect
        v-model="selectedInbox"
        track-by="id"
        label="name"
        :placeholder="$t('AUTOMATION.ACTION.SMS_INBOX_DROPDOWN_PLACEHOLDER')"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :max-height="160"
        :options="inboxOptions"
        :allow-empty="false"
      />
    </div>
    <textarea
      v-model="message"
      rows="4"
      class="mb-0 resize-y"
      :placeholder="$t('AUTOMATION.ACTION.SMS_MESSAGE_INPUT_PLACEHOLDER')"
    />
    <p class="mb-0 text-xs text-n-slate-11 sms-counter">
      {{
        $t('AUTOMATION.ACTION.SMS_COUNTER', {
          length: counter.length,
          limit: counter.limit,
          segments: counter.segments,
        })
      }}
    </p>
  </div>
</template>

<style scoped>
.multiselect {
  margin: var(--space-smaller) var(--space-zero);
}
</style>
